import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { MenuState } from '../lib/dashboard-types';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import './telemetry-components.css';

interface FloatingMenuProps {
  menu: MenuState | null;
  onClose: () => void;
}

export const FloatingMenu: React.FC<FloatingMenuProps> = ({ menu, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Calculate Y position based on menu target position, clamped to viewport bottom
  const calculatedY = useMemo(() => {
    if (!menu) return 0;
    let initialY = menu.y;
    const initialH = Math.min((menu.title ? 36 : 0) + (menu.items?.length >= 12 ? 36 : 0) + (menu.items?.length || 0) * 36 + 16, window.innerHeight * 0.55);
    if (initialY + initialH > window.innerHeight - 12) {
      initialY = Math.max(8, window.innerHeight - initialH - 12);
    }
    return initialY;
  }, [menu?.x, menu?.y, menu?.title, menu?.items?.length]);

  // Reset search when menu title/items change
  useEffect(() => {
    setSearchQuery('');
  }, [menu?.title, menu?.items]);

  useEffect(() => {
    const handlePointerDown = (e: PointerEvent | MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('mousedown', handlePointerDown, true);
    window.addEventListener('keydown', handleKey, true);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('mousedown', handlePointerDown, true);
      window.removeEventListener('keydown', handleKey, true);
    };
  }, [onClose]);

  // Filter items based on search query
  const filteredItems = menu?.items.filter((item) => {
    if (!searchQuery.trim()) return true;
    return item.label.toLowerCase().includes(searchQuery.toLowerCase());
  }) ?? [];

  const handleItemClick = (item: any, e: React.MouseEvent) => {
    if (!item.disabled) item.onClick(e);
  };
  if (!menu) return null;
  const showSearch = menu.items.length >= 12;
  return (
    <Fragment>
      {/* Full-screen transparent backdrop barrier: closes menu on click and prevents background page scroll */}
      <div
        id="telemetry-floating-menu-backdrop"
        className="td-floating-menu-backdrop"
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
        onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onTouchMove={(e) => { e.preventDefault(); e.stopPropagation(); }}
      />
      <div
        id="telemetry-floating-menu"
        ref={ref}
        className="td-floating-menu"
        style={{
          left: menu.x,
          top: calculatedY,
          width: menu.width || 220,
          maxHeight: 'min(420px, 60vh)',
          overflowY: 'auto',
        }}
        data-nodrag
      >
        {(menu.title || showSearch) && (
          <div className="td-floating-menu-top">
            {menu.title && (
              <div
                id="telemetry-floating-menu-title"
                className="td-floating-menu-title"
              >
                {menu.onBack && (
                  <CustomToolTip title="Back to Subsystems" placement="top">
                    <button
                      type="button"
                      id="telemetry-floating-menu-back-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        menu.onBack?.();
                      }}
                      className="td-floating-menu-back-btn"
                    >
                      ←
                    </button>
                  </CustomToolTip>
                )}
                <span>{menu.title}</span>
              </div>
            )}
            {showSearch && (
              <div className="td-modal-search-wrap" style={{ padding: '2px 4px 4px' }}>
                <input
                  id="telemetry-floating-menu-search-input"
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="td-modal-search-input"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    className="td-modal-search-clear-btn"
                    style={{ right: 10, top: 6 }}
                    onClick={() => setSearchQuery('')}
                  >
                    ✕
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {filteredItems.length === 0 && (
          <div id="telemetry-floating-menu-empty" className="td-modal-empty-msg" style={{ padding: '12px 8px' }}>
            No items match "{searchQuery}"
          </div>
        )}

        {filteredItems.map((item, i) => {
          const modifierClass = item.disabled
            ? 'td-floating-menu-item--disabled'
            : item.danger
              ? 'td-floating-menu-item--danger'
              : '';

          const slug = item.label ? item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-') : `item-${i}`;

          return (
            <CustomToolTip
              key={`${item.label}-${i}`}
              title={item.label}
              placement="right"
              className="td-floating-menu-label"
              style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
            >
              <button
                id={`telemetry-floating-menu-item-${slug}`}
                onClick={(e) => handleItemClick(item, e)}
                className={`td-floating-menu-item ${modifierClass}`}
                disabled={item.disabled}
              >
                <span className="td-floating-menu-content">
                  {item.icon && <span className="td-floating-menu-icon">{item.icon}</span>}
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                    {item.label}
                  </span>
                </span>
                {item.checked != null && (
                  <span className={`td-floating-menu-check ${item.checked ? 'td-floating-menu-check--active' : ''}`}>
                    {item.checked ? '✓' : ''}
                  </span>
                )}
              </button>
            </CustomToolTip>

          );
        })}
      </div>
    </Fragment>
  );
};

export default FloatingMenu;
